import { format } from 'date-fns';

import commonMixin from '@/mixins/templates/common';
import contentContextMixin from '@/mixins/content-context';

import { parseDate } from '@/helpers/date';
import { gradeTypes } from '@/helpers/grades';
import ContentType from '@/helpers/contentType';
import ConditionalTypes from '@/helpers/availabilityConditionalTypes';
import { resolveTerm } from '@/helpers/terms';
import AcademicMainType from '@/helpers/academicMainType';

export default {
  name   : 'AvailabilityBanner',
  mixins : [commonMixin, contentContextMixin],
  props  : {
    conditionals: {
      type    : Array,
      default : null
    },
    isBlockedContentByLicense: {
      type     : Boolean,
      required : false
    },
    isAccessCodeEnabled: {
      type     : Boolean,
      required : false,
      default  : false
    }
  },
  computed: {
    hasStudentRestriction() {
      return this.studentTypeConditionalIcon && this.studentTypeConditionalText && !this.getterUserRoleContext.isStudent;
    },
    hasGradeRestriction() {
      return this.conditionalGradeItems && this.conditionalGradeItems.length;
    },
    isAcademicMainTypeCourse() {
      const context = this.getterContent.context || this.getterCourse.context;
      return context.academicMainTypeId === AcademicMainType.COURSE;
    }
  },
  data() {
    return {
      gradeTypes,
      initialDateRestrictionText : '',
      finalDateRestrictionText   : '',
      studentTypeConditionalText : '',
      studentTypeConditionalIcon : '',
      conditionalGradeItems      : null,
      showAcademicMainDateError  : false
    };
  },
  mounted() {
    if (!this.conditionals) {
      return;
    }

    const handleUserOrGroup = (conditional) => {

      [this.studentTypeConditionalIcon, this.studentTypeConditionalText] = {

        [ConditionalTypes.USER]: ['mdi-account-outline', this.$t('conditionalAvailability.thereAreConditionalsByStudent')],

        [ConditionalTypes.MAIN_GROUP]: ['mdi-account-group-outline', this.$t('conditionalAvailability.thereAreConditionalsByStudentGroup')]

      }[conditional.typeId];

    };

    const handleConditionalType = {
      [ConditionalTypes.DATE_MANUAL]: (conditional) => {
        this.initialDateRestrictionText = conditional.startAt ? `${this.$t('conditionalAvailability.openAt')} : ${parseDate(conditional.startAt)}` : '';
        this.finalDateRestrictionText = conditional.endAt ? parseDate(conditional.endAt) : '';
      },
      [ConditionalTypes.DATE_ACADEMIC_MAIN]: (conditional) => {

        if (!conditional.calculatedStartAt) {
          this.showAcademicMainDateError = this.isAcademicMainTypeCourse && !this.getterUserRoleContext.isStudent;
          return;
        }

        const parsedDate = parseDate(`${conditional.calculatedStartAt.slice(0, 10)} ${conditional.calculatedStartAt.slice(11, 16)}`);

        this.initialDateRestrictionText = `${this.$t('conditionalAvailability.openAt')} : ${parsedDate}`;
      },
      [ConditionalTypes.GRADE]: (conditional) => {
        this.conditionalGradeItems = conditional.grades;
      },
      [ConditionalTypes.DATE_FIRST_ACCESS]: (conditional) => {
        this.mountFirstAccessConditionalText(conditional);
      },
      [ConditionalTypes.USER]                    : handleUserOrGroup,
      [ConditionalTypes.MAIN_GROUP]              : handleUserOrGroup,
      [ConditionalTypes.DATE_CREATED_ENROLLMENT] : (conditional) => {
        this.mountEnrollmentRegistrationConditionalText(conditional);
      }
    };

    this.conditionals.forEach(conditional => {
      handleConditionalType[conditional.typeId](conditional);
    });
  },
  methods: {
    mountFirstAccessConditionalText(conditional) {
      const { formattedHour, formattedMinutes } = this.formatTime(conditional.startTime);
      const messageTimeSegments = this.getMessageTimeSegments(conditional);

      let descriptiveText;
      const afterFirstAccess = this.$t(`conditionalAvailability.opensAfterFirstAccess${this.getterUserRoleContext.isStudent ? 'Student' : ''}`, [resolveTerm('course').toLowerCase()]);
      if (messageTimeSegments.length === 1) {
        descriptiveText = `${messageTimeSegments[0]} ${afterFirstAccess}`;
      } else {
        const lastItem = messageTimeSegments.pop();
        descriptiveText = `${messageTimeSegments.join(', ')} ${this.$t('conditionalAvailability.and')} ${lastItem} ${afterFirstAccess}`;
      }

      if (!this.getterUserRoleContext.isStudent) {
        this.initialDateRestrictionText = `${this.$t('conditionalAvailability.openAt')} : ${descriptiveText}`;
        return;
      }

      const firstAccessAt = new Date(this.context.firstAccessAt);
      firstAccessAt.setDate(firstAccessAt.getDate() + conditional.daysAfterStart);
      firstAccessAt.setHours(firstAccessAt.getHours() + +formattedHour);
      firstAccessAt.setMinutes(firstAccessAt.getMinutes() + +formattedMinutes);
      this.initialDateRestrictionText = `${this.$t('conditionalAvailability.openAt')} : ${parseDate(firstAccessAt.toISOString())}<br>(${descriptiveText})`;
    },
    getRedirectToTopicUrl(conditionalGradeItem) {
      let url = `/course/${this.$route.params.courseId}/content/`;

      const isInsidePage = ContentType.isInsidePage(conditionalGradeItem.parentTopicId, conditionalGradeItem.parentTopicTypeId);
      if (isInsidePage) {
        const topicToFocusHash = `#${conditionalGradeItem.topicId}`;
        url += conditionalGradeItem.parentTopicId + topicToFocusHash;
      } else {
        url += conditionalGradeItem.topicId;
      }

      return url;
    },
    redirectToTopic(conditionalGradeItem) {
      const topicIsInCurrentPage = Number(this.$route.params.id) === conditionalGradeItem.parentTopicId;
      if (topicIsInCurrentPage) {
        location.hash = '';
        location.hash = `#${conditionalGradeItem.topicId}`;
        this.$emit('closeAvailabilityModal');
      } else {
        const url = this.getRedirectToTopicUrl(conditionalGradeItem);
        this.$router.push(url);
      }
    },
    getGradeItemValue(conditionalGradeItem) {
      return typeof conditionalGradeItem.minValue === 'number' ? conditionalGradeItem.minValue : conditionalGradeItem.maxValue;
    },
    getGradeValueText(conditionalGradeItem) {
      const gradeValue = this.getGradeItemValue(conditionalGradeItem);
      const formattedGradeValue = this.$options.filters.formatNumberWithDecimals(gradeValue);

      return this.$tc('conditionalAvailability.points', gradeValue, { value: formattedGradeValue });
    },
    mountEnrollmentRegistrationConditionalText(conditional) {
      const { formattedHour, formattedMinutes } = this.formatTime(conditional.startTime);

      if (this.getterUserRoleContext.isStudent) {
        const parsedDate = parseDate(`${conditional.calculatedStartAt.slice(0, 10)} ${conditional.calculatedStartAt.slice(11, 16)}`);
        this.initialDateRestrictionText = `${this.$t('conditionalAvailability.openAt')} : ${parsedDate}`;
        return;
      }

      const messageTimeSegments = this.getMessageTimeSegments(conditional);

      const releasedItemText = this.$t('conditionalAvailability.releasedItem');
      const afterRegistrationDateText = this.$t('conditionalAvailability.afterRegistrationDate');

      const parts = messageTimeSegments.filter(Boolean).reduce((acc, segment, index, array) => {
        if (index === array.length - 1 && array.length > 1) {
          return `${acc} ${this.$t('conditionalAvailability.and')} ${segment}`;
        }
        return index === 0 ? segment : `${acc}, ${segment}`;
      }, '');

      this.initialDateRestrictionText = `${releasedItemText} ${parts} ${afterRegistrationDateText}`;
    },
    getMessageTimeSegments(conditional) {
      const { formattedHour, formattedMinutes } = this.formatTime(conditional.startTime);
      const messageTimeSegments = [];

      if (conditional.daysAfterStart) {
        const days = this.$tc('conditionalAvailability.opensAfterDays', [conditional.daysAfterStart], { days: conditional.daysAfterStart });
        messageTimeSegments.push(days);
      }
      if (formattedHour !== '0') {
        const hours = this.$tc('conditionalAvailability.opensAfterHours', [+formattedHour], { hours: formattedHour });
        messageTimeSegments.push(hours);
      }
      if (formattedMinutes !== '0') {
        const minutes = this.$tc('conditionalAvailability.opensAfterMinutes', [+formattedMinutes], { minutes: formattedMinutes });
        messageTimeSegments.push(minutes);
      }

      return messageTimeSegments;
    },
    formatTime(time) {
      const [hour, minutes] = time?.split(":") || ['00', '00'];
      return {
        formattedHour    : hour.replace(/^0+/, '') || '0',
        formattedMinutes : minutes.replace(/^0+/, '') || '0'
      };
    }
  }
};

